/** List-local display defaults: newest first, cancelled rows hidden unless asked for. */

/** Newest first by an ISO date/datetime string (missing dates sort last). Returns a new array. */
export const sortByDateDesc = <T,>(rows: T[], getDate: (row: T) => string | null | undefined): T[] =>
    [...rows].sort((a, b) => (getDate(b) ?? '').localeCompare(getDate(a) ?? ''));

/** Drops CANCELLED rows unless `show` is true. */
export const hideCancelled = <T,>(rows: T[], getStatus: (row: T) => string | null | undefined, show = false): T[] =>
    show ? rows : rows.filter(r => getStatus(r) !== 'CANCELLED');

/** Local today as yyyy-MM-dd (not UTC). */
const localToday = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Newest document first, but rows dated in the future (after `today`) never lead the list: they
 * follow the dated-up-to-today rows, soonest first. Missing dates go last.
 */
export const sortByDateDescFutureLast = <T,>(
    rows: T[], getDate: (row: T) => string | null | undefined, today: string = localToday(),
): T[] => {
    const dated = (r: T) => (getDate(r) ?? '').slice(0, 10);
    const past = sortByDateDesc(rows.filter(r => dated(r) && dated(r) <= today), dated);
    const future = rows.filter(r => dated(r) > today).sort((a, b) => dated(a).localeCompare(dated(b)));
    const undated = rows.filter(r => !dated(r));
    return [...past, ...future, ...undated];
};
